'use client';

import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import NextLink from 'next/link';
import { useSyncExternalStore, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Button } from './button';
import { useHydrated } from './use-hydrated';

export type ResponsiveBreakpoint = 'md' | 'lg';

const QUERY: Record<ResponsiveBreakpoint, string> = {
  md: '(min-width: 768px)',
  lg: '(min-width: 1024px)',
};

// One subscribe fn per query: a fresh fn each render makes useSyncExternalStore re-subscribe matchMedia every render.
const subscribers = new Map<string, (onChange: () => void) => () => void>();

function subscribeTo(query: string) {
  let subscribe = subscribers.get(query);
  if (!subscribe) {
    subscribe = (onChange) => {
      if (typeof window === 'undefined' || !window.matchMedia) return () => {};
      const mql = window.matchMedia(query);
      mql.addEventListener?.('change', onChange);
      return () => mql.removeEventListener?.('change', onChange);
    };
    subscribers.set(query, subscribe);
  }
  return subscribe;
}

/**
 * True from `breakpoint` up. Server snapshot is `false` (phone-first), so SSR
 * and the first client render agree; CSS (`hidden md:block`) does the visual
 * switch, this only decides which copy assistive tech sees.
 */
export function useIsAtLeast(breakpoint: ResponsiveBreakpoint = 'md'): boolean {
  const query = QUERY[breakpoint];
  return useSyncExternalStore(
    subscribeTo(query),
    () =>
      typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false,
    () => false,
  );
}

export type ResponsiveTableColumn<Row> = {
  key: string;
  header: ReactNode;
  render: (row: Row) => ReactNode;
  className?: string;
  /** Hide this column (table layout only) below the given breakpoint. */
  hideBelow?: ResponsiveBreakpoint;
};

export type ResponsiveTableAction = {
  key: string;
  label: ReactNode;
  /** Runs the action. Omit when `href` navigates instead. */
  onSelect?: () => void;
  /** Renders the item as a link (real navigation: open in new tab, copy address). */
  href?: string;
  destructive?: boolean;
  disabled?: boolean;
};

export type ResponsiveTablePrimaryAction = {
  label: ReactNode;
  onSelect: () => void;
  disabled?: boolean;
  /** Shows the button's spinner and disables it (a request for this row is in flight). */
  loading?: boolean;
  /** Native tooltip, e.g. why the button is disabled. */
  title?: string;
};

export type ResponsiveTableProps<Row> = {
  columns: ResponsiveTableColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  /** The below-breakpoint layout for one row. */
  renderCard: (row: Row) => ReactNode;
  /** Table from this breakpoint up, cards below it. Default `md`. */
  breakpoint?: ResponsiveBreakpoint;
  caption?: string;
  emptyState?: ReactNode;
  /** Per-row actions, shown as a kebab menu in both layouts. Empty list: no kebab. */
  rowActions?: (row: Row) => ResponsiveTableAction[];
  /**
   * The row's one visible primary action (e.g. Confirm). A small button beside
   * the kebab from the breakpoint up, a full-width 44px button on the card.
   * Return null for rows that have none.
   */
  primaryAction?: (row: Row) => ResponsiveTablePrimaryAction | null;
  /** Accessible name of the kebab trigger (required with `rowActions`). */
  actionsLabel?: string;
  className?: string;
};

const HIDE_BELOW: Record<ResponsiveBreakpoint, string> = {
  md: 'hidden md:table-cell',
  lg: 'hidden lg:table-cell',
};

const SHOW_FROM: Record<ResponsiveBreakpoint, string> = {
  md: 'hidden md:block',
  lg: 'hidden lg:block',
};

const HIDE_FROM: Record<ResponsiveBreakpoint, string> = {
  md: 'md:hidden',
  lg: 'lg:hidden',
};

function RowMenu({
  actions,
  label,
  touch,
}: {
  actions: ResponsiveTableAction[];
  label?: string;
  touch: boolean;
}) {
  if (actions.length === 0) return null;
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={label}
          className={cn(
            'text-muted hover:bg-surface hover:text-foreground focus-visible:ring-primary/40 inline-flex shrink-0 items-center justify-center rounded-md outline-none focus-visible:ring-2',
            touch ? 'size-11 min-h-11 min-w-11 md:size-9 md:min-h-0 md:min-w-0' : 'size-9',
          )}
        >
          <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          // Don't hand focus back to the trigger; the chosen action decides.
          onCloseAutoFocus={(e) => e.preventDefault()}
          className="bg-card border-border text-foreground z-50 min-w-[10rem] rounded-md border p-1 shadow-md"
        >
          {actions.map((a) => {
            const className = cn(
              'hover:bg-surface focus:bg-surface block min-h-11 cursor-pointer rounded px-3 py-2 text-sm outline-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 md:min-h-0',
              a.destructive && 'text-danger',
            );
            return a.href ? (
              <DropdownMenu.Item key={a.key} disabled={a.disabled} asChild className={className}>
                <NextLink href={a.href}>{a.label}</NextLink>
              </DropdownMenu.Item>
            ) : (
              <DropdownMenu.Item
                key={a.key}
                disabled={a.disabled}
                // Run after the menu has closed and Radix has restored focus, so an
                // action that mounts an autoFocus editor keeps that focus.
                onSelect={() => setTimeout(() => a.onSelect?.(), 0)}
                className={className}
              >
                {a.label}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/**
 * A real `<table>` from `breakpoint` up, a list of cards below it. Both trees
 * render (CSS decides which is visible, so SSR is right); the hidden one is
 * `aria-hidden` + `inert` so assistive tech reads one copy.
 */
export function ResponsiveTable<Row>({
  columns,
  rows,
  rowKey,
  renderCard,
  breakpoint = 'md',
  caption,
  emptyState,
  rowActions,
  primaryAction,
  actionsLabel,
  className,
}: ResponsiveTableProps<Row>) {
  const wide = useIsAtLeast(breakpoint);
  const hydrated = useHydrated();
  // Both trees only for SSR + first paint; after mount render just the live one.
  const showTable = !hydrated || wide;
  const showCards = !hydrated || !wide;

  const primaryButton = (row: Row, card: boolean) => {
    const action = primaryAction?.(row);
    if (!action) return null;
    return (
      <Button
        type="button"
        size="sm"
        disabled={action.disabled}
        loading={action.loading}
        title={action.title}
        onClick={action.onSelect}
        className={card ? 'min-h-11 w-full' : undefined}
      >
        {action.label}
      </Button>
    );
  };

  if (rows.length === 0 && emptyState) {
    return <div className={className}>{emptyState}</div>;
  }

  return (
    <div className={className}>
      {showTable && (
        // Scrolls sideways when the columns cannot fit, so the last ones stay reachable.
        <div
          className={cn(SHOW_FROM[breakpoint], 'overflow-x-auto')}
          aria-hidden={!wide || undefined}
          inert={!wide}
        >
          <table className="w-full caption-bottom text-sm">
            {caption && <caption className="sr-only">{caption}</caption>}
            <thead>
              <tr className="border-border border-b">
                {columns.map((c) => (
                  <th
                    key={c.key}
                    scope="col"
                    className={cn(
                      'text-muted px-3 py-3 text-left align-middle font-medium',
                      c.hideBelow && HIDE_BELOW[c.hideBelow],
                      c.className,
                    )}
                  >
                    {c.header}
                  </th>
                ))}
                {(rowActions || primaryAction) && (
                  <th scope="col" className="w-12 px-3 py-3">
                    <span className="sr-only">{actionsLabel}</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={rowKey(row)} className="border-border border-b last:border-0">
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={cn(
                        'px-3 py-3 align-top',
                        c.hideBelow && HIDE_BELOW[c.hideBelow],
                        c.className,
                      )}
                    >
                      {c.render(row)}
                    </td>
                  ))}
                  {(rowActions || primaryAction) && (
                    <td className="px-3 py-3 text-right align-top">
                      <div className="flex items-center justify-end gap-2">
                        {primaryButton(row, false)}
                        {rowActions && (
                          <RowMenu actions={rowActions(row)} label={actionsLabel} touch={false} />
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCards && (
        <ul
          role="list"
          className={cn('space-y-3', HIDE_FROM[breakpoint])}
          aria-hidden={wide || undefined}
          inert={wide}
        >
          {rows.map((row) => (
            <li
              key={rowKey(row)}
              className="bg-card border-border flex items-start gap-2 rounded-xl border p-3"
            >
              <div className="min-w-0 flex-1 space-y-2">
                {renderCard(row)}
                {primaryButton(row, true)}
              </div>
              {rowActions && <RowMenu actions={rowActions(row)} label={actionsLabel} touch />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
